import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Utensils, CalendarDays, Clock, Users, Loader2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/booking";

export default function TableManage() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/table-bookings/manage/${token}`);
      setBooking(res.data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const handleCancel = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/table-bookings/manage/${token}/cancel`);
      setBooking(res.data);
      toast.success("Your table booking has been cancelled.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="h-8 w-8 animate-spin text-[#722F37]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6" data-testid="table-not-found">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-[#722F37] mx-auto" />
          <h1 className="mt-4 font-serif text-3xl text-[#1C1917]">Booking not found</h1>
          <p className="mt-2 text-[#57534E]">This management link is invalid or has expired.</p>
          <Link to="/" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#722F37] text-white font-semibold">Back to site</Link>
        </div>
      </div>
    );
  }

  const cancelled = booking.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6" data-testid="table-manage-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#1C1917] mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-full bg-[#722F37]/10 flex items-center justify-center">
              <Utensils className="h-6 w-6 text-[#722F37]" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-[#1C1917]">Your Friday Night Wine Bar booking</h1>
              <p className="text-sm text-[#57534E]">Gledswood Cellar Door</p>
            </div>
          </div>

          {cancelled ? (
            <div className="rounded-xl bg-[#722F37]/8 border border-[#722F37]/20 px-4 py-3 mb-6 flex items-center gap-2 text-[#722F37] font-medium" data-testid="table-cancelled-badge">
              <XCircle className="h-5 w-5" /> This table booking is cancelled.
            </div>
          ) : (
            <div className="rounded-xl bg-[#4A5D23]/8 border border-[#4A5D23]/20 px-4 py-3 mb-6 flex items-center gap-2 text-[#4A5D23] font-medium">
              <CheckCircle2 className="h-5 w-5" /> Confirmed
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Date</p>
              <p className="mt-1 font-medium text-[#1C1917]">{fmtDate(booking.date)}</p>
            </div>
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Arrival</p>
              <p className="mt-1 font-medium text-[#1C1917]">{booking.arrival_time}</p>
            </div>
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
              <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Party</p>
              <p className="mt-1 font-medium text-[#1C1917]">{booking.party_size} people</p>
            </div>
          </div>

          <p className="mt-4 text-sm text-[#57534E]">Booked under {booking.name} · {booking.email}</p>

          {!cancelled && (
            <button
              onClick={handleCancel}
              disabled={busy}
              data-testid="table-cancel-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#722F37] text-[#722F37] font-semibold hover:bg-[#722F37] hover:text-white transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Cancel table booking
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
